import { useState, useEffect, memo } from 'react';
import { X, Clock, Copy, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Timer display component - only re-renders when time changes
const TimerDisplay = memo(({ timeLeft }: { timeLeft: { days: number; hours: number; minutes: number; seconds: number } }) => {
  const formatNumber = (num: number) => num.toString().padStart(2, '0');
  
  return (
    <motion.div 
      className="flex items-center gap-1 mb-3"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.4 }}
    >
      <Clock className="h-3.5 w-3.5 text-brand-lighter" />
      <div className="flex text-xs gap-1 text-brand-lighter">
        <span>{formatNumber(timeLeft.days)}d</span>
        <span>:</span>
        <span>{formatNumber(timeLeft.hours)}h</span>
        <span>:</span>
        <span>{formatNumber(timeLeft.minutes)}m</span>
        <span>:</span>
        <motion.span 
          className="animate-pulse"
          animate={{ opacity: [1, 0.5, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
        >
          {formatNumber(timeLeft.seconds)}s
        </motion.span>
        <span className="ml-1">remaining</span>
      </div>
    </motion.div>
  );
});

TimerDisplay.displayName = 'TimerDisplay';

// Copy button component - memoized to prevent re-renders
const CopyButton = memo(({ promoCode, copyStatus, onCopy }: { 
  promoCode: string; 
  copyStatus: boolean; 
  onCopy: () => void;
}) => {
  return (
    <motion.div 
      className="bg-white rounded-md p-2 flex items-center justify-between mb-3"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, delay: 0.5 }}
    >
      <div className="text-black font-mono font-bold tracking-wider">{promoCode}</div>
      <motion.button 
        className="bg-gray-100 hover:bg-gray-200 text-black p-1.5 rounded transition-colors"
        onClick={onCopy}
        aria-label={copyStatus ? 'Copied' : 'Copy code'}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {copyStatus ? (
            <motion.div
              key="check"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Check className="h-4 w-4" />
            </motion.div>
          ) : (
            <motion.div
              key="copy"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Copy className="h-4 w-4" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </motion.div>
  );
});

CopyButton.displayName = 'CopyButton';

export default function PromoBanner() {
  useEffect(() => {
    localStorage.removeItem('promoBannerClosed');
  }, []);
  
  const [isVisible, setIsVisible] = useState(true);
  const [copyStatus, setCopyStatus] = useState(false);
  const promoCode = "SPRING100";
  
  const [timeLeft, setTimeLeft] = useState({
    days: 3,
    hours: 12,
    minutes: 30,
    seconds: 0
  });
  
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        let { days, hours, minutes, seconds } = prev;
        
        if (seconds > 0) {
          seconds -= 1;
        } else {
          seconds = 59;
          if (minutes > 0) {
            minutes -= 1;
          } else {
            minutes = 59;
            if (hours > 0) {
              hours -= 1;
            } else {
              hours = 23;
              if (days > 0) {
                days -= 1;
              }
            }
          }
        }
        
        if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
          clearInterval(timer);
        }
        
        return { days, hours, minutes, seconds };
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, []);
  
  const handleClose = () => {
    setIsVisible(false);
    localStorage.setItem('promoBannerClosed', 'true');
  };
  
  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(promoCode);
      setCopyStatus(true);
      setTimeout(() => setCopyStatus(false), 2000);
      return;
    }
    
    const el = document.createElement('textarea');
    el.value = promoCode;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopyStatus(true);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed bottom-4 right-4 z-50 w-80 shadow-xl rounded-lg overflow-hidden"
        initial={{ opacity: 0, y: 100, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 100, scale: 0.8 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <div className="bg-gradient-to-br from-brand to-brand-dark text-white relative">
          <div className="flex items-center justify-between pt-3 px-4">
            <motion.div 
              className="bg-brand-light/60 rounded-full px-2 py-0.5 text-xs font-medium"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              Limited Time Offer
            </motion.div>
            <motion.button 
              className="text-white/80 hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors"
              onClick={handleClose}
              aria-label="Close"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <X className="h-4 w-4" />
            </motion.button>
          </div>
          
          <div className="p-4 pt-1">
            <motion.h3 
              className="text-xl font-bold mb-1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              <span className="font-light">$100 OFF</span> <br />
              <span className="font-bold">Spring Sale</span>
            </motion.h3>
            
            <TimerDisplay timeLeft={timeLeft} />
            
            <CopyButton 
              promoCode={promoCode}
              copyStatus={copyStatus}
              onCopy={handleCopy}
            />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}